import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavbarComponent } from "../../shared/components/navbar/navbar.component";
import { FooterComponent } from "../../shared/components/footer/footer.component";
import { AuthService } from '@auth0/auth0-angular';
import { ApiService } from 'src/app/core/services/api.service';
import { switchMap, tap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { Router } from '@angular/router';
import { packages } from 'src/app/core/models/packages';
import { NavigationRailComponent } from '../../shared/components/navigation-rail/navigation-rail.component';
import { ManualReportsService } from 'src/app/core/services/manual-reports.service';
import { HttpClientModule } from '@angular/common/http';
import { isReportReady } from 'src/app/core/models/report-readiness';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NavbarComponent,
    FooterComponent,
    MatProgressSpinnerModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    NavigationRailComponent
    ,HttpClientModule
  ]
})
export class ReportsComponent implements OnInit {
  auth = inject(AuthService);
  apiService = inject(ApiService);
  manualReportsService = inject(ManualReportsService);
  router = inject(Router);

  displayedColumns: string[] = ['Email', 'Type', 'Name', 'Report', 'CreateDate', 'Score', 'Actions'];
  diligences: any[] = [];
  manualReports: any[] = [];
  workspaceReports: any[] = [];
  loading = false;
  loadError = false;
  userId: string = '';

  ngOnInit(): void {
    this.loading = true;
    this.loadError = false;

    this.auth.user$.pipe(
      tap(user => {
        if (user?.sub) {
          this.userId = user.sub;
        }
      }),
      switchMap(user => {
        if (user?.email) {
          return this.apiService.getDiligencesByEmail();
        }
        else {
          throw new Error('user not logged in');
        }
      })
    ).subscribe({
      next: diligences => {
        this.loading = false;
        this.diligences = diligences?.dueDiligences ?? [];
        this.manualReports = diligences?.manualReports ?? [];
        const manualReportsById = new Map(this.manualReports.map((report: any) => [report.uuid, report]));
        const ordinaryReports = this.diligences.filter((report: any) => {
          const manualReport = manualReportsById.get(report.uuid);
          return !manualReport || (!manualReport.pdfAvailable && isReportReady(report));
        }).map((report: any) => {
          const manualReport = manualReportsById.get(report.uuid);
          return manualReport && isReportReady(report)
            ? {
                ...report,
                package: manualReport.package,
                vesselName: manualReport.vesselName,
                imoNumber: manualReport.imoNumber,
                email: report.user_email ?? manualReport.email
              }
            : report;
        });
        const readyAutomatedIds = new Set(
          ordinaryReports.filter((report: any) => isReportReady(report)).map((report: any) => report.uuid)
        );
        const manualWorkspaceReports = this.manualReports
          .filter((report: any) => !readyAutomatedIds.has(report.uuid))
          .map((report: any) => ({ ...report, isManual: true }));
        this.workspaceReports = [...ordinaryReports, ...manualWorkspaceReports];
      },
      error: () => {
        this.diligences = [];
        this.loadError = true;
        this.loading = false;
      }
    });
  }

  getDiligenceName(diligence: any) {
    const report = diligence?.report;
    const packageName = typeof report === 'string'
      ? report
      : report?.package ?? report?.report ?? diligence?.package;
    return packages.find(p => p.code === packageName || p.name === packageName)?.name ?? packageName;
  }

  getDiligenceTarget(diligence: any): string {
    return diligence?.vesselName
      ?? diligence?.report?.target?.name
      ?? diligence?.report?.name
      ?? diligence?.target?.name
      ?? diligence?.name
      ?? '—';
  }

  getDiligenceSubtitle(diligence: any): string {
    const imo = diligence?.imoNumber ?? diligence?.report?.target?.imoNumber ?? diligence?.report?.imoNumber;
    const email = diligence?.email ?? diligence?.report?.email ?? diligence?.user_email;
    if (imo) return `IMO ${imo}${email ? ` · ${email}` : ''}`;
    return [diligence?.type, email].filter(Boolean).join(' · ') || '—';
  }

  getManualPackage(report: any): string { return packages.find(p => p.code === report.package)?.name ?? report.package ?? 'Manual report'; }

  isReportReady(report: any): boolean { return isReportReady(report); }

  downloadManualReport(uuid: string): void {
    this.manualReportsService.downloadPdf(uuid).subscribe({ error: () => { this.loadError = true; } });
  }

  openDiligence(uuid: string) {
    this.router.navigate(['/dueDiligence', uuid]);
  }
}
